import { useTranslation } from 'module/hooks/use-translation';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import {
  ExpandableSearchInput,
  ExpandableSearchInputRef,
} from 'module/ui/components/atoms/input/search-input';
import { useSearchValueState } from 'module/ui/components/atoms/input/search-value-state';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { PageLoading } from 'module/ui/components/atoms/loading';
import { HeaderWithIconTitle } from 'module/ui/components/molecules/page/page';
import { Argon } from 'module/ui/components/templates/argon';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { faBook } from '@fortawesome/pro-solid-svg-icons';
import { Button } from '@rocketlane/ui';
import { KnowledgeBaseFolderLite } from 'api/knowledge-base/types';
import {
  useAddAiPagesMutation,
  useAddWebsiteToKnowledgeBaseMutation,
  useCreateKnowledgeBaseAttachmentMutation,
  useCreateKnowledgeBaseFolderMutation,
  useKnowledgeBaseFoldersQuery,
  useUpdateKnowledgeBaseFolderMutation,
} from 'queries/knowledge-base';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { AiPagesPayload } from 'components/organisms/knowledge-base/add-ai-pages';
import { AddFilesPayload } from 'components/organisms/knowledge-base/add-files';
import { AddKnowledgeTriggerType } from 'components/organisms/knowledge-base/add-knowledge-trigger';
import { AddWebsitePayload } from 'components/organisms/knowledge-base/add-website';
import {
  CreateKnowledgeBaseFolderDrawer,
  KnowledgeBaseFolderDrawerPayload,
} from 'components/organisms/knowledge-base/create-knowledge-base/create-knowledge-base-folder';
import { NoKnowledgeBases } from 'components/organisms/knowledge-base/empty-states/no-folders';
import { NoSearchResult } from 'components/organisms/knowledge-base/empty-states/no-search-result';
import { KnowledgeBaseFolderList } from 'components/organisms/knowledge-base/list';
import { useKnowledgeBaseNavigation } from 'components/organisms/knowledge-base/navigation-utils';

const ContentWrapper = styled.div`
  padding: 0 var(--spacing-05);
`;

export const KnowledgeBasePage = () => {
  const title = useTranslation('knowledge_base.page.title');
  const searchPlaceholder = useTranslation('search');
  const searchInputRef = useRef<ExpandableSearchInputRef>(null);
  const { value: searchQuery, onChange: onSearchChange } =
    useSearchValueState();
  const {
    data: knowledgeBaseFolders,
    isLoading: isKnowledgeBaseFoldersLoading,
  } = useKnowledgeBaseFoldersQuery();
  const isEmpty = (knowledgeBaseFolders ?? []).length === 0;
  const filteredKnowledgeBaseFolders = useMemo(() => {
    return (knowledgeBaseFolders ?? []).filter((folder) =>
      folder.folderName.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [knowledgeBaseFolders, searchQuery]);
  const clearSearch = useCallback(() => {
    searchInputRef.current?.closeSearchInput();
  }, []);
  const { navigateToKnowledgeBaseFolder } = useKnowledgeBaseNavigation();
  const { mutateAsync: createKnowledgeBaseFolder } =
    useCreateKnowledgeBaseFolderMutation();
  const { mutateAsync: updateKnowledgeBaseFolder } =
    useUpdateKnowledgeBaseFolderMutation();
  const [
    createKnowledgeBaseFolderModalVisible,
    setCreateKnowledgeBaseFolderModalVisible,
  ] = useState(false);
  const [editingFolder, setEditingFolder] =
    useState<KnowledgeBaseFolderLite | null>(null);
  const showCreateKnowledgeBaseFolderModal = useCallback(() => {
    setEditingFolder(null);
    setCreateKnowledgeBaseFolderModalVisible(true);
  }, []);
  const closeCreateKnowledgeBaseFolderModal = useCallback(() => {
    setCreateKnowledgeBaseFolderModalVisible(false);
    setEditingFolder(null);
  }, []);
  const showEditKnowledgeBaseFolderModal = useCallback(
    (folder: KnowledgeBaseFolderLite) => {
      setCreateKnowledgeBaseFolderModalVisible(false);
      setEditingFolder(folder);
    },
    [],
  );
  const { mutateAsync: addWebsiteToKnowledgeBase } =
    useAddWebsiteToKnowledgeBaseMutation();
  const handleAddWebsite = useCallback(
    async (folderId: number, payload: AddWebsitePayload) => {
      await addWebsiteToKnowledgeBase({
        folder_id: folderId,
        source_url: payload.websiteUrl,
        selected_nodes: payload.selectedFolderPaths,
        content_type: payload.apiDocVersion ? 'API_DOC' : 'WEBSITE',
      });
    },
    [addWebsiteToKnowledgeBase],
  );
  const { mutateAsync: uploadFiles } =
    useCreateKnowledgeBaseAttachmentMutation();
  const handleUpload = useCallback(
    async (folderId: number, payload: AddFilesPayload) => {
      await Promise.all(
        payload.files.map(async (file) => {
          await uploadFiles({
            folderId,
            file: file.originFileObj,
            info: {
              name: file.name,
              publicVisibility: true,
            },
          });
        }),
      );
    },
    [uploadFiles],
  );
  const { mutateAsync: addAiPagesApi } = useAddAiPagesMutation();
  const handleAddAiPages = useCallback(
    async (folderId: number, payload: AiPagesPayload) => {
      await addAiPagesApi({
        folder_id: folderId,
        templateIds: payload.selectedTemplates,
        filter: payload.filter,
      });
    },
    [addAiPagesApi],
  );
  const handleDone = useCallback(
    async (payload: KnowledgeBaseFolderDrawerPayload) => {
      if (editingFolder) {
        await updateKnowledgeBaseFolder({
          folderId: editingFolder.folderId,
          payload: {
            folderName: payload.folderName,
            folderDescription: payload.folderDescription,
          },
        });
        setEditingFolder(null);

        return;
      }

      const createdFolder = await createKnowledgeBaseFolder({
        folderName: payload.folderName,
        folderDescription: payload.folderDescription,
      });

      if (
        payload.contentType === AddKnowledgeTriggerType.ImportWebsite ||
        payload.contentType === AddKnowledgeTriggerType.ApiDoc
      ) {
        await handleAddWebsite(
          createdFolder.folderId,
          payload.contentPayload as AddWebsitePayload,
        );
      } else if (payload.contentType === AddKnowledgeTriggerType.Files) {
        await handleUpload(
          createdFolder.folderId,
          payload.contentPayload as AddFilesPayload,
        );
      } else if (payload.contentType === AddKnowledgeTriggerType.AiPages) {
        await handleAddAiPages(
          createdFolder.folderId,
          payload.contentPayload as AiPagesPayload,
        );
      }

      navigateToKnowledgeBaseFolder(createdFolder.folderId);
      setCreateKnowledgeBaseFolderModalVisible(false);
    },
    [
      createKnowledgeBaseFolder,
      editingFolder,
      handleAddAiPages,
      handleAddWebsite,
      handleUpload,
      navigateToKnowledgeBaseFolder,
      updateKnowledgeBaseFolder,
    ],
  );

  return (
    <Argon
      title={
        <HeaderWithIconTitle
          icon={<FontAwesomeIcon icon={faBook} />}
          title={title}
        />
      }
      documentTitle={title}
      content={
        <div>
          {isKnowledgeBaseFoldersLoading ? (
            <PageLoading />
          ) : isEmpty ? (
            <NoKnowledgeBases
              onPrimaryAction={showCreateKnowledgeBaseFolderModal}
            />
          ) : filteredKnowledgeBaseFolders.length > 0 ? (
            <ContentWrapper>
              <KnowledgeBaseFolderList
                folders={filteredKnowledgeBaseFolders}
                onEdit={showEditKnowledgeBaseFolderModal}
              />
            </ContentWrapper>
          ) : (
            <NoSearchResult onPrimaryAction={clearSearch} />
          )}
          <CreateKnowledgeBaseFolderDrawer
            open={
              createKnowledgeBaseFolderModalVisible || editingFolder != null
            }
            onClose={closeCreateKnowledgeBaseFolderModal}
            onDone={handleDone}
            folder={editingFolder}
          />
        </div>
      }
      actions={{
        secondary: isEmpty ? (
          <></>
        ) : (
          <CenterAlignedBox>
            <ExpandableSearchInput
              ref={searchInputRef}
              direction="right"
              value={searchQuery}
              onChange={onSearchChange}
              searchButtonProps={{
                type: 'text',
              }}
              minWidth="360px"
              placeholder={searchPlaceholder}
            />
            <Button
              variant="primary"
              type="button"
              leftIcon={<FontAwesomeIcon icon={faPlus} size={12} />}
              onClick={showCreateKnowledgeBaseFolderModal}
            >
              <FormattedMessage id="knowledge_base.action.create_knowledge_base" />
            </Button>
          </CenterAlignedBox>
        ),
      }}
    />
  );
};
